/**
 * English: Inbox
 *
 * What this file covers: Conversations with clients on every channel: replying, notes, assigning, taking over
 * from the chatbot, canned replies.
 *
 * Tone: Quick and practical, for people answering clients all day. Short labels. When something cannot be sent,
 * say why and what to do, in one sentence.
 *
 * English: English is the reference: every key is added here first, in South African English (organise, colour,
 * cancelled). Every other language falls back to this text for any key it does not have yet.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const inbox: Catalog = {
  'inbox.title': 'Inbox',
  'inbox.assignedTo': 'Assigned to {{name}}.',
  'inbox.unassignedToast': 'Conversation unassigned.',
  'inbox.resolvedToast': 'Conversation resolved.',
  'inbox.reopenedToast': 'Conversation reopened.',
  'inbox.formerAgent': 'A team member who has left',
  'inbox.context.title': 'Contact',
  'inbox.context.open': 'Contact',
  'inbox.context.notInBook': 'Not in your contact book yet.',
  'inbox.context.addContact': 'Add to contacts',
  'inbox.context.handoffs': 'Hand-offs',
  'inbox.context.handoff': '{{from}} → {{to}}',
  'inbox.empty.hint': 'Attach a WhatsApp number and conversations will appear here as people message you.',
  'inbox.empty.action': 'Attach a number',
  'inbox.empty.filtered': 'Nothing matches these filters',
  'inbox.empty.filtered.hint': 'Try a different queue, state or search term.',
  'inbox.filters.clear': 'Clear filters',
  'inbox.filters.queue': 'Queue',
  'inbox.filters.state': 'State',
  'inbox.filters.state.any': 'Any state',
  'inbox.queue.all': 'All',
  'inbox.queue.mine': 'Mine',
  'inbox.queue.unassigned': 'Unassigned',
  'inbox.search.placeholder': 'Search name, number or message',
  'inbox.search.label': 'Search conversations',
  'inbox.list.label': 'Conversations',
  'inbox.loadError': 'Conversations could not be loaded. This is not the same as having none.',
  'inbox.loadMore': 'Load more',
  'inbox.loadMore.of': 'Load more ({{shown}} of {{total}})',
  'inbox.jumpToLatest': 'Jump to latest',
  'inbox.unread.count': '{{count}} unread',
  'inbox.messages.empty': 'No messages yet',
  'inbox.messages.empty.hint': 'Nothing has been sent or received on this conversation.',
  'inbox.messages.error': 'This conversation could not be loaded.',
  'inbox.message.sending': 'Sending…',
  'inbox.message.delivered': 'Delivered',
  'inbox.message.read': 'Read',
  'inbox.message.failed': 'Not delivered',
  'inbox.message.failed.code': 'Not delivered (code {{code}})',
  'inbox.send.noCredits': 'Not enough credit to send. Top up the wallet to carry on.',
  'inbox.send.numberInactive': 'That WhatsApp number is no longer active.',
  'inbox.resolve.confirm.title': 'Resolve with unread messages?',
  'inbox.resolve.confirm.body': 'This conversation still has {{count}} unread. Resolving it takes it out of the queue.',
  'inbox.window.closed': 'Reply window closed',
  'inbox.window.closed.tooltip': 'More than 24 hours since the last message from this person. WhatsApp only accepts an approved template now.',
  'inbox.window.closed.title': 'The 24-hour reply window has closed',
  'inbox.window.closed.body': 'WhatsApp only accepts an approved template more than 24 hours after someone last wrote to you. Send a template to reopen the conversation.',
  'inbox.window.closed.action': 'Go to templates',
  'inbox.window.open.tooltip': 'How long you can still reply in your own words before only templates are accepted.',
  'inbox.window.openHours': '{{hours}}h {{minutes}}m left to reply',
  'inbox.window.openMinutes': '{{minutes}}m left to reply',
  'inbox.description': 'Live WhatsApp conversations. Replies inside the 24-hour window send as free-form text.',
  'inbox.empty': 'No conversations yet: attach a number and have a recipient message you.',
  'inbox.select': 'Select a conversation to read its history and reply.',
  'inbox.noMessages': 'No messages yet',
  'inbox.loadingMessages': 'Loading messages…',
  'inbox.assign': 'Assign agent',
  'inbox.unassigned': 'Unassigned',
  'inbox.assigned': 'Assignment updated.',
  'inbox.lock': 'Lock',
  'inbox.locked': 'Conversation locked to you.',
  'inbox.lockedBy': 'Locked by',
  'inbox.anotherAgent': 'another agent',
  'inbox.send': 'Send',
  'inbox.reply.placeholder': 'Type a reply… (/ for canned replies)',
  'inbox.newline': 'for a new line',
  'inbox.canned.hint': 'inserts a canned reply',
  'inbox.canned.noMatch': 'No canned replies match.',
  'inbox.canned.manage': 'Manage canned replies',
  'inbox.canned.navigate': 'navigate',
  'inbox.canned.insert': 'insert',
  'inbox.canned.close': 'close',
  'inbox.tags.title': 'Tags',
  'inbox.tags.add': 'Add tag',
  'inbox.tags.placeholder': 'Tag name…',
  'inbox.tags.suggestions': 'Existing tags',
  'inbox.transfer': 'Transfer',
  'inbox.transfer.title': 'Transfer conversation',
  'inbox.transfer.agent': 'Transfer to',
  'inbox.transfer.note': 'Note for the receiving agent (optional)',
  'inbox.transfer.noteprefix': 'Transferred to',
  'inbox.transfer.keeps': 'Notes and tags stay with the conversation.',
  'inbox.transfer.done': 'Conversation transferred.',
  'inbox.transfer.noAgents': 'Nobody else is on your team yet. Invite people under Settings, Team.',
  'inbox.sla.firstReply': 'First reply',
  'inbox.sla.resolve': 'Resolve',
  'inbox.sla.left': 'left',
  'inbox.sla.over': 'over',
  'inbox.sla.breached': 'SLA breached',
  'inbox.sla.met': 'SLA met',
  'inbox.sla.due': 'Due',
  'inbox.notes.title': 'Internal notes',
  'inbox.notes.empty': 'No notes yet.',
  'inbox.notes.agent': 'Agent',
  'inbox.notes.placeholder': 'Add an internal note (not sent to the contact)…',
  'inbox.notes.add': 'Add note',
  'inbox.lock.heldBy': 'Locked by',
  'inbox.lock.until': 'until',
  'inbox.lock.mine': 'You hold this conversation',
  'inbox.lock.release': 'Release',
  'inbox.lock.released': 'Lock released.',
  'inbox.lock.readOnly': 'Read-only while another agent is replying.',
  'inbox.resolve': 'Resolve',
  'inbox.reopen': 'Reopen',
  'inbox.stateChanged': 'Conversation updated.',
  'inbox.state.pending': 'Pending',
  'inbox.state.open': 'Open',
  'inbox.state.resolved': 'Resolved',
  'inbox.state.archived': 'Archived',
  'canned.title': 'Canned replies',
  'canned.description': 'Reusable replies your agents insert by typing "/" in the inbox composer.',
  'canned.new': 'New reply',
  'canned.edit': 'Edit reply',
  'canned.shortcut': 'Shortcut',
  'canned.shortcutHint': 'Lower-case letters, digits, dots, dashes or underscores: agents type /shortcut.',
  'canned.titleField': 'Title',
  'canned.body': 'Message',
  'canned.bodyHint': 'Sent exactly as written (max 4096 characters).',
  'canned.language': 'Language',
  'canned.search': 'Search shortcuts or titles…',
  'canned.empty': 'No canned replies yet.',
  'canned.emptyHint': 'Create one and agents can insert it with "/" in the inbox.',
  'canned.shortcutTaken': 'That shortcut is already in use.',
  'canned.invalid': 'Shortcut and message are required.',
  'canned.saved': 'Canned reply saved.',
  'canned.created': '/{{shortcut}} created.',
  'canned.discard.title': 'Discard your changes?',
  'canned.discard.body': 'You have unsaved edits to this reply.',
  'canned.discard.confirm': 'Discard them',
  'canned.discard.keep': 'Keep editing',
  'canned.language.any': 'Any language',
  'canned.deleted': 'Canned reply deleted.',
  'canned.deleteConfirm': 'Delete this canned reply? Agents can no longer insert it.',
  'canned.updated': 'Updated',
  'inbox.assignFailed': 'Assign failed',
  'inbox.cannedReplies.keyEnter': 'Enter',
  'inbox.cannedReplies.keyEsc': 'Esc',
  'inbox.keyShift': 'Shift',
  'inbox.lockFailed': 'Lock failed',
  'inbox.notes.addFailed': 'Could not add note.',
  'inbox.releaseFailed': 'Release failed',
  'inbox.sendFailed': 'Send failed',
  'inbox.tags.addFailed': 'Could not add tag.',
  'inbox.tags.removeFailed': 'Could not remove tag.',
  'inbox.transfer.transferFailed': 'Transfer failed',
  'inbox.updateFailed': 'Update failed',
  'inbox.conversations': 'Conversations',
  'inbox.filters.more': 'More filters',
  'inbox.queue.open': 'Open',
  'inbox.queue.resolved': 'Resolved',
  'inbox.showingOf': 'Showing {{shown}} of {{total}}',
  'inbox.sla': 'SLA',
  'inbox.tags': 'Tags',
  'inbox.assignedTo.label': 'Assigned to',
  'inbox.composer.emoji': 'Insert an emoji',
  'inbox.composer.canned': 'Insert a canned reply',
  'inbox.composer.hint': 'Press Enter to send, Shift+Enter for a new line',
  'inbox.message.sent': 'Sent',
  'inbox.notes.private': 'Private',
  'inbox.quickReplies.title': 'Quick replies',
  'inbox.quickReplies.search': 'Search replies',
  'inbox.quickReplies.empty': 'No canned replies yet.',
  'inbox.tip': 'Tip: type / in the reply box for canned replies, and press Enter to send.',
  'inbox.sla.onTrack': 'On track',
  'inbox.sla.dueSoon': 'Due soon',
  'inbox.replyWindow': 'Reply window',
  'inbox.attachment.open': 'View',
  'inbox.attachment.unavailable': 'The file could not be kept when it arrived.',
  'inbox.attachment.kind.image': 'Photo',
  'inbox.attachment.kind.video': 'Video',
  'inbox.attachment.kind.audio': 'Voice note',
  'inbox.attachment.kind.document': 'Document',
  'inbox.attachment.kind.sticker': 'Sticker',
  'inbox.spend': 'Spent on this contact',
  'inbox.spendHint': '{{count}} charges: messages, bot replies and model tokens. Opens usage.',
  'inbox.newMessage': 'New message',
  'inbox.message.failed.WINDOW_CLOSED': 'Not delivered: they have not written to you in 24 hours, so this needed an approved template.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE': 'Not delivered: WhatsApp turned this down over a billing problem on our side, not yours. We are fixing it.',
  'inbox.message.failed.UNDELIVERABLE': 'Not delivered: WhatsApp could not deliver this to that number.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED': 'Not delivered: the number you sent from is not registered with WhatsApp.',
  'inbox.message.failed.MEDIA_NOT_FETCHED': 'Not delivered: WhatsApp could not fetch the attached file.',
  'inbox.message.failed.META_REFUSED': 'Not delivered: WhatsApp accepted this and then did not deliver it.',
  'inbox.message.failed.notCharged': 'You were not charged for this.',
  'inbox.message.template.marker': 'Template · {{name}}',
  'inbox.message.template.noWording': 'The wording of this template message was not recorded when it was sent.',
  'inbox.message.template.noWording.named': 'The wording of “{{name}}” was not recorded when this went out.',
  'inbox.message.template.buttons': 'Buttons on this message',
  'inbox.message.template.header.IMAGE': 'Picture at the top',
  'inbox.message.template.header.VIDEO': 'Video at the top',
  'inbox.message.template.header.DOCUMENT': 'File at the top',
  'inbox.message.template.header.LOCATION': 'Location at the top',
  'inbox.message.failed.WINDOW_CLOSED.next': 'Send them an approved template instead. Once they reply you can write freely again for another 24 hours.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.next': 'You do not need to do anything at Meta. Messages start going through again as soon as we have sorted it out.',
  'inbox.message.failed.UNDELIVERABLE.next': 'Check the number is right and that it uses WhatsApp. If it is correct, the person may have blocked messages from your business.',
  'inbox.message.failed.NUMBER_NOT_REGISTERED.next': 'Open Numbers and reconnect this number. If it asks for a code, we will walk you through the verification.',
  'inbox.message.failed.MEDIA_NOT_FETCHED.next': 'Send it again with the file uploaded here rather than linked, or check that the link is public and still works.',
  'inbox.message.failed.META_REFUSED.next': 'Try again in a few minutes. If it keeps happening, tell us and we will look into it.',
  'inbox.details.title': 'Message details',
  'inbox.details.open': 'What happened to this message',
  'inbox.details.close': 'Close message details',
  'inbox.details.error': 'The details of this message could not be loaded.',
  'inbox.details.timeline': 'What happened',
  'inbox.details.timeline.sent': 'Sent',
  'inbox.details.timeline.received': 'Received',
  'inbox.details.timeline.delivered': 'Delivered',
  'inbox.details.timeline.read': 'Read',
  'inbox.details.timeline.failed': 'Not delivered',
  'inbox.details.timeline.later': '{{duration}} later',
  'inbox.details.read.absent': 'No read receipt. WhatsApp only sends one when the recipient has read receipts switched on, so this can stay empty even after they have read your message.',
  'inbox.details.delivered.pending': 'WhatsApp has not confirmed delivery yet.',
  'inbox.details.why': 'Why it did not arrive',
  'inbox.details.why.metaCode': 'WhatsApp code',
  'inbox.details.why.metaWords': 'WhatsApp said',
  'inbox.details.cost': 'What it cost',
  'inbox.details.cost.unknown': 'We could not read the ledger for this message, so we cannot say what it cost.',
  'inbox.details.cost.rate': 'Rate',
  'inbox.details.cost.worked': 'Worked out as',
  'inbox.details.cost.workedValue': '{{units}} × {{rate}}',
  'inbox.details.cost.chargedAt': 'Charged',
  'inbox.details.cost.refunded': 'Refunded {{amount}} because WhatsApp did not deliver this message.',
  'inbox.details.sent': 'What was sent',
  'inbox.details.template': 'Template',
  'inbox.details.language': 'Language',
  'inbox.details.category': 'Category',
  'inbox.details.wording': 'Wording',
  'inbox.details.wording.recovered': 'Recovered from the template as it stands now, so it may differ from what was sent.',
  'inbox.details.wording.lost': 'Not recorded when this was sent, and the template is no longer on file.',
  'inbox.details.params': 'Values filled in',
  'inbox.details.param.header': 'Heading',
  'inbox.details.param.body': 'Message',
  'inbox.details.param.button': 'Button',
  'inbox.details.file.name': 'File name',
  'inbox.details.file.type': 'File type',
  'inbox.details.file.size': 'File size',
  'inbox.details.where': 'Where it went',
  'inbox.details.whereFrom': 'Where it came from',
  'inbox.details.to': 'To',
  'inbox.details.from': 'From',
  'inbox.details.sentBy': 'Sent by',
  'inbox.details.sentBy.you': 'You',
  'inbox.details.sentBy.teammate': 'A team member',
  'inbox.details.sentBy.bot': 'Your chatbot',
  'inbox.details.sentBy.broadcast': 'A broadcast',
  'inbox.details.sentBy.platform': 'RepletoryReach',
  'inbox.details.support': 'For support',
  'inbox.details.metaId.hint': 'Quote this if you ask WhatsApp about this message.',
  'inbox.details.copy': 'Copy',
  'inbox.details.copied': 'Copied',
  'inbox.channel.whatsapp': 'WhatsApp',
  'inbox.channel.messenger': 'Messenger',
  'inbox.channel.instagram': 'Instagram',
  'inbox.window.closed.body.messenger': 'Messenger only delivers a reply within 24 hours of the person\'s last message, and that time has passed. Your reply will reach them if they write again.',
  'inbox.window.closed.body.instagram': 'Instagram only delivers a reply within 24 hours of the person\'s last message, and that time has passed. Your reply will reach them if they write again.',
  'inbox.send.notMetered': 'The message was sent, but we could not record what it cost. Support has been told; nothing has been charged to you twice.',
  'inbox.sidebar.title': 'Conversation settings',
  'inbox.sidebar.toggle': 'Settings',
  'inbox.sidebar.close': 'Close conversation settings',
  'inbox.sidebar.details': 'Details',
  'inbox.sidebar.shortcuts': 'Keyboard shortcuts',
  'inbox.notes.visibility': 'Only visible to your team',
  'inbox.lock.hint': 'Lock this conversation so nobody else replies while you do',
  'inbox.shortcuts.send': 'Send the reply',
  'inbox.shortcuts.newline': 'Start a new line',
  'inbox.shortcuts.canned': 'Open canned replies',
  'inbox.shortcuts.pick': 'Move through canned replies',
  'inbox.shortcuts.insert': 'Insert the highlighted reply',
  'inbox.shortcuts.dismiss': 'Close the canned replies list',
  'inbox.shortcuts.key.enter': 'Enter',
  'inbox.shortcuts.key.shiftEnter': 'Shift+Enter',
  'inbox.shortcuts.key.slash': '/',
  'inbox.shortcuts.key.arrows': '↑ ↓',
  'inbox.shortcuts.key.enterTab': 'Enter or Tab',
  'inbox.shortcuts.key.esc': 'Esc',
  'inbox.bot.label': 'Bot',
  'inbox.bot.paused': 'Bot paused',
  'inbox.bot.active': 'Bot answering',
  'inbox.bot.resumesAt': 'The bot answers again from {{time}}, unless the team replies before then.',
  'inbox.bot.untilResolved': 'The bot stays quiet until this conversation is resolved or you resume it.',
  'inbox.bot.activeHint': 'The bot answers new messages. Replying yourself takes the conversation over: the bot then stays quiet until it is resolved or handed back.',
  'inbox.bot.resume': 'Resume bot',
  'inbox.bot.pause': 'Pause bot',
  'inbox.bot.resumed': 'The bot will answer this conversation again.',
  'inbox.bot.pausedToast': 'The bot is paused on this conversation.',
  'inbox.bot.failed': 'The bot could not be changed on this conversation.',
  'inbox.block.label': 'Customer',
  'inbox.block.blocked': 'Blocked',
  'inbox.block.notBlocked': 'Not blocked',
  'inbox.block.action': 'Block customer',
  'inbox.block.unblock': 'Unblock',
  'inbox.block.title': 'Block this customer?',
  'inbox.block.body': 'The bot will not answer them and nobody on your team can write to them until you unblock them. Their messages are still kept here.',
  'inbox.block.reason': 'Reason',
  'inbox.block.reasonHint': 'For your team. The customer does not see it.',
  'inbox.block.alsoWhatsapp': 'Also block this number on WhatsApp',
  'inbox.block.alsoWhatsappHint': 'They will not be able to message your number at all. WhatsApp only blocks someone who wrote in the last 24 hours.',
  'inbox.block.confirm': 'Block',
  'inbox.block.done': 'The customer is blocked.',
  'inbox.block.doneWhatsapp': 'The customer is blocked here and on WhatsApp.',
  'inbox.block.whatsappFailed': 'Blocked here, but WhatsApp did not block the number: {{error}}',
  'inbox.block.unblocked': 'The customer is unblocked.',
  'inbox.block.failed': 'The customer could not be blocked or unblocked.',
  'inbox.block.bar': 'You blocked this customer. The bot does not answer them and replies are turned off.',
  'inbox.block.since': 'Blocked {{time}}',
  'inbox.block.onWhatsapp': 'Also blocked on WhatsApp',
  'inbox.channel.voice': 'Phone call',
  'inbox.voice.readOnly.title': 'A record of phone calls',
  'inbox.voice.readOnly.body': 'Nothing can be sent from here. To answer, call the person back from your phone, or write to them on WhatsApp.',
  'inbox.voice.openCall': 'Open the call',
  'inbox.channel.web': 'Website chat',
  'inbox.takeover.takeOver': 'Take over',
  'inbox.takeover.handBack': 'Hand back to the bot',
  'inbox.takeover.handBackTo': 'Hand back to {{bot}}',
  'inbox.takeover.with': 'With {{name}}',
  'inbox.takeover.you': 'you',
  'inbox.takeover.someone': 'a teammate',
  'inbox.takeover.theBot': 'the bot',
  'inbox.takeover.done': 'You have the chat. The customer was told you joined, and the bot stays out until you hand it back.',
  'inbox.takeover.handedBack': 'Handed back to {{bot}}. The customer was told, and the chat stays in your queue.',
  'inbox.takeover.failed': 'That did not work. Try again.',
  'inbox.takeover.noticeFailed': 'The customer could not be told: {{error}}',
  'inbox.channel.email': 'Email',
  'inbox.email.subjectLine': 'Subject: {{subject}}',
  'inbox.send.ownAccountNotOnPlan': 'Your plan no longer includes your own WhatsApp account. Move to Growth or Scale to keep sending from this number.',
  'inbox.send.dailyLimit': 'This number has reached WhatsApp\'s limit of {{limit}} new people in 24 hours. People already messaged today can still be reached.',
  'inbox.send.dailyLimit.shared': 'You have reached your share of today\'s limit on our shared WhatsApp account: {{share}} new people in 24 hours. Unused room is shared out after 18:00, and people already messaged today can still be reached.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.customer': 'Not delivered: your WhatsApp account has no working payment method in Meta.',
  'inbox.message.failed.ACCOUNT_NOT_PAYABLE.customer.next': 'Add a payment method in Meta\'s payment settings for your WhatsApp account. Messages start going through again once Meta can bill it.',
  'inbox.team.label': 'Team',
  'inbox.team.none': 'No team',
  'inbox.team.former': 'A team that was removed',
  'inbox.team.hint': 'Only this team\'s people are given this conversation.',
  'inbox.team.moved': 'Moved to {{name}}.',
  'inbox.team.cleared': 'Taken out of its team.',
  'inbox.team.failed': 'The team could not be changed.',
  'inbox.filters.team': 'Team',
  'inbox.filters.team.any': 'Any team',
  'inbox.filters.team.none': 'No team',
  'inbox.book': 'Book',
  'inbox.book.done': 'Booked {{number}} for {{when}}.',
  'inbox.ticket.create': 'Create ticket',
  'inbox.ticket.created': 'Ticket {{number}} created for this conversation.',
  'inbox.ticket.chipHint': 'Linked ticket: {{subject}}',
  'inbox.ticket.chipLabel': 'Ticket {{number}}, {{status}}. Open the ticket.',
  'inbox.available.label': 'Take new conversations',
  'inbox.available.hint': 'Conversations nobody has yet are shared out in turn among the people with this on, within the conversation\'s team when it has one. Turning it off keeps the conversations you already have.',
  'inbox.available.short': 'takes new conversations',
  'inbox.moreActions': 'More actions',
  'inbox.lock.menu': 'Lock while I reply',
  'inbox.pageBroken.title': 'Replies cannot be sent on this Page',
  'inbox.pageBroken.body': 'Facebook stopped accepting the Page\'s connection. Their messages still arrive here, but nothing you send reaches them until the Page is connected again under Channels.',
  'inbox.pageBroken.action': 'Connect again',
  'inbox.message.unsupported.title': 'WhatsApp did not pass this message on',
  'inbox.message.unsupported.body': 'It is a kind of message WhatsApp does not yet deliver to business software, such as a login code with a copy button, so we never received what it said. If it was a code from Facebook or another service, ask for it by SMS or email instead.',
  'inbox.message.unsupported.preview': 'A message WhatsApp did not pass on',
  'inbox.pageBroken.manychat': 'ManyChat stopped accepting the key for this Page. Their messages still arrive here, but nothing you send reaches them until a new ManyChat key is pasted under Channels.',
  'inbox.mediaText.transcript': 'Transcript:',
  'inbox.mediaText.photo': 'What your assistant saw:',
  'inbox.mediaText.document': 'The document reads:',
  'inbox.attachment.kind.attachment': 'A file sent through ManyChat'
};

export default inbox;
